/**
 * Sinalização da queda do ChatContar fora do corpo da página: título da aba
 * piscando e notificação nativa.
 *
 * No ChatContar desktop, `window.Notification` é interceptado pelo Electron
 * (electron-chat/main.cjs) e vira toast do sistema + botão piscando na barra
 * de tarefas. O flash da barra para quando a janela ganha foco, por isso
 * reavisamos de tempos em tempos enquanto a queda não for resolvida.
 *
 * O texto do título evita dígitos entre parênteses de propósito: o Electron lê
 * `(N)` do título como contador de mensagens não lidas.
 */

type EstadoDeAlerta = 'disconnected' | 'expired';

const INTERVALO_PISCA_MS = 1500;
const REAVISO_MS = 2 * 60 * 1000;

let interval: ReturnType<typeof setInterval> | null = null;
let tituloBase = '';
let estadoAtual: EstadoDeAlerta | null = null;
let ultimoAvisoEm = 0;

const TITULO_ALERTA = '⚠ ChatContar desconectado';

function textos(estado: EstadoDeAlerta) {
  return estado === 'expired'
    ? {
        titulo: 'ChatContar: sessão expirada',
        corpo: 'Entre novamente para voltar a receber mensagens.',
      }
    : {
        titulo: 'ChatContar desconectado',
        corpo: 'Você não está recebendo novas mensagens. Abra o ChatContar e clique em Reconectar.',
      };
}

function notificar(estado: EstadoDeAlerta) {
  ultimoAvisoEm = Date.now();
  try {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    const { titulo, corpo } = textos(estado);
    const n = new Notification(titulo, {
      body: corpo,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: 'chatcontar-conexao',
      ...({ renotify: true } as NotificationOptions),
    });
    n.onclick = () => {
      try { window.focus(); } catch { /* ignore */ }
      try { n.close(); } catch { /* ignore */ }
    };
  } catch {
    // ignore
  }
}

function comFoco() {
  if (typeof document === 'undefined') return false;
  if (document.visibilityState !== 'visible') return false;
  return typeof document.hasFocus === 'function' ? document.hasFocus() : true;
}

/** Liga (ou atualiza) a sinalização de queda. Idempotente. */
export function startChatDisconnectSignal(estado: EstadoDeAlerta) {
  if (typeof document === 'undefined') return;

  if (estadoAtual !== estado) {
    estadoAtual = estado;
    notificar(estado);
  }

  if (interval) return;

  tituloBase = document.title === TITULO_ALERTA ? tituloBase : document.title;
  // Marca já no primeiro instante: esperar o primeiro tique deixaria a aba com
  // cara de normal justamente no momento em que o chat acabou de cair.
  document.title = TITULO_ALERTA;
  let liga = true;
  interval = setInterval(() => {
    liga = !liga;
    document.title = liga ? TITULO_ALERTA : tituloBase;
    // Sem foco na janela, o flash da barra de tarefas já morreu — reavisa para
    // que o alerta continue visível para quem está em outro programa.
    if (estadoAtual && !comFoco() && Date.now() - ultimoAvisoEm > REAVISO_MS) {
      notificar(estadoAtual);
    }
  }, INTERVALO_PISCA_MS);
}

/** Desliga a sinalização e devolve o título original da aba. */
export function stopChatDisconnectSignal() {
  if (interval) {
    clearInterval(interval);
    interval = null;
  }
  estadoAtual = null;
  ultimoAvisoEm = 0;
  if (typeof document !== 'undefined' && tituloBase && document.title === TITULO_ALERTA) {
    document.title = tituloBase;
  }
}
